import * as R from "ramda";
import React, { useEffect, useRef, useState } from "react";
import { percentToXYGauge } from "../../../helper/manipulateGauge"


interface Point {
  x: number,
  y: number
}

interface DownloadGaugeData {
  sweepFlag: number,
  gaugeXY: Point,
  gaugeXYInner: Point
  gaugeXYLarge: Point
}

const calcDownloadGaugeValues = (valuePercent: number): DownloadGaugeData => {    
  const gaugeXY = percentToXYGauge(valuePercent, 85);
  const gaugeXYLarge = percentToXYGauge(valuePercent + 0.35, 100);
  const gaugeXYInner = percentToXYGauge(valuePercent + 0.35, 78);
  const sweepFlag = valuePercent < 62 ? 0 : 1;
  return {
      gaugeXY,
      gaugeXYInner,
      gaugeXYLarge,
      sweepFlag,
  };
};

const calcDownloadGaugePathData = (valuePercent: number): string => {
  const downloadGaugeData = calcDownloadGaugeValues(valuePercent);
  return `M 53.705682023722694 171.28699827536104 A 85 85 0 ${downloadGaugeData.sweepFlag} 1 ${downloadGaugeData.gaugeXY.x} ${downloadGaugeData.gaugeXY.y}`;
}

const calcDownloadGaugeIndicatorPathData = (valuePercent: number): string => {
  const downloadGaugeData = calcDownloadGaugeValues(valuePercent);
  return `M ${downloadGaugeData.gaugeXYInner.x} ${downloadGaugeData.gaugeXYInner.y} L ${downloadGaugeData.gaugeXYLarge.x} ${downloadGaugeData.gaugeXYLarge.y}`;
}

const calcPercentValue = (value: number, maxValue: number, round: boolean): number => {
  const k = round ? Math.round((50 * value) / maxValue) : (50 * value) / maxValue;
  const valuePercent = Math.min(100, k * 2);
  return valuePercent;
}

const generatePercentValues = (prevValue: number, nextValue: number, maxValue: number, round: boolean): number[] => {
  const count = 42;
  const firstValue = calcPercentValue(prevValue, maxValue, true);
  const lastValue = calcPercentValue(nextValue, maxValue, true);
  const step = (lastValue - firstValue) / count;    
  return R.range(0, count).map(n => firstValue + step * n).map(value => round ? Math.round(value) : value);
}

const generateAnimatedValues = (prevValue: number, nextValue: number, maxValue: number): string => {
  return generatePercentValues(prevValue, nextValue, maxValue, true).map(p => calcDownloadGaugePathData(p)).join(";");
}

const generateIndicatorAnimatedValues = (prevValue: number, nextValue: number, maxValue: number): string => {
  return generatePercentValues(prevValue, nextValue, maxValue, false).map(p => calcDownloadGaugeIndicatorPathData(p)).join(";");
}

interface InternalGaugeProps {
  prevValue: number;
  nextValue: number;
  maxValue: number;
}

const InternalGauge: React.FC<InternalGaugeProps> = ({ prevValue, nextValue, maxValue }) => {

  const dur = "0.7s";
  const calcMode = "discrete";
  const keySplines = ".5 0 .5 1"
  const values = generateAnimatedValues(prevValue, nextValue, maxValue);
  const indicatorValues = generateIndicatorAnimatedValues(prevValue, nextValue, maxValue);

  const svgRef1 = useRef<SVGAnimateElement>(null);
  const svgRef2 = useRef<SVGAnimateElement>(null);

  useEffect(() => {
      svgRef1.current?.beginElement();
      svgRef2.current?.beginElement();
  }, [prevValue, nextValue]);

  return (
      <svg
          className="gaugeNew"
          viewBox="0 0 200 200"
          fill="none"
          xmlns="https://www.w3.org/2000/svg"
          style={{ width: 190, height: 190, paddingTop: 10 }}
      >
          <path stroke="#333" strokeWidth="14" strokeDasharray="1.75 7">
              <animate ref={svgRef1} fill="freeze" attributeName="d" dur={dur} values={values} calcMode={calcMode} keySplines={keySplines} />
          </path>
          <path stroke="#F00" strokeWidth="4">
              <animate ref={svgRef2} fill="freeze" attributeName="d" dur={dur} values={indicatorValues} calcMode={calcMode} keySplines={keySplines} />
          </path>
      </svg>
  );
}

interface GaugeProps {
  speed_bps: number | null
  maxSpeed_bps: number
}

const Gauge: React.FC<GaugeProps> = ({ speed_bps, maxSpeed_bps }) => {
  const [oldValue, setOldValue] = useState(0);
  const [prevValue, setPrevValue] = useState<number | null>(null);

  useEffect(() => {
      const newPrevValue = oldValue;
      setOldValue(speed_bps === null ? 0 : speed_bps);
      setPrevValue(newPrevValue);
  }, [speed_bps]);

  return (
      <svg
          className="gaugeNew"
          viewBox="0 0 200 200"
          fill="none"
          xmlns="https://www.w3.org/2000/svg"
          style={{ width: 190, height: 190, paddingTop: 10 }}
      >
          <path stroke="#ccc" fill="none" strokeWidth="14"
              d="M 53.705682023722694 171.28699827536104 A 85 85 0 1 1 146.2943179762773 171.28699827536104"
              strokeDasharray="1.75 7"
          />
          {(!R.isNil(prevValue) && !R.isNil(speed_bps)) && (
              <InternalGauge prevValue={prevValue} nextValue={speed_bps} maxValue={maxSpeed_bps} />
          )}
      </svg>
  );
}

export default Gauge;
